import { useCallback, useEffect, useState } from 'react';

import { AddBankAccountButton } from '@perpay-web/fintech/components/composite/AddBankAccountButton/AddBankAccountButton';
import BankAccountDisplay from '@perpay-web/fintech/components/composite/BankAccountDisplay/BankAccountDisplay';
import LoadingAccountDisplay from '@perpay-web/fintech/components/composite/BankAccountDisplay/LoadingAccountDisplay';
import { ChooseBankModal } from '@perpay-web/fintech/components/composite/ChooseBankModal/ChooseBankModal';
import { useLoadBankAccounts } from '@perpay-web/fintech/hooks/useLoadBankAccounts';
import { firstItemOf } from '@perpay-web/utils/arrayUtils';
import { noop } from '@perpay-web/utils/noop';

/**
 * @param {Object} props
 * @param {Function} props.onChange
 * @param {boolean} [props.skipMFA]
 * @param {('card')} [props.buttonTheme] - The theme of the button to use for the add bank account button.
 * @param {string} [props.prefix] - The prefix to display before the bank account name
 * @param {Function} [props.onAddBankAccount] - The function to call when a bank account is added
 * @returns {JSX.Element}
 */
export const ChooseBankAccountButton = ({
    onChange = noop,
    buttonTheme,
    skipMFA,
    prefix,
    onAddBankAccount,
}) => {
    const { bankAccounts, isLoading, fetchBankAccounts } =
        useLoadBankAccounts();
    const [selectedBankAccount, setSelectedBankAccount] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (!bankAccounts || !bankAccounts.length || selectedBankAccount) {
            return;
        }

        const primaryBA =
            bankAccounts.find((ba) => ba.isPrimary) ||
            firstItemOf(bankAccounts);
        setSelectedBankAccount(primaryBA);
        onChange(primaryBA);
    }, [bankAccounts, selectedBankAccount, onChange]);

    const handleSelectBankAccount = useCallback(
        (bank) => {
            setSelectedBankAccount(bank);
            onChange(bank);
        },
        [onChange],
    );

    const handleAddBankAccount = useCallback(
        (bank) => {
            handleSelectBankAccount(bank);
            if (onAddBankAccount) {
                onAddBankAccount(bank);
            }
        },
        [handleSelectBankAccount, onAddBankAccount],
    );

    if (isLoading) {
        return <LoadingAccountDisplay prefix={prefix} />;
    }

    return (
        <>
            {selectedBankAccount ? (
                <BankAccountDisplay
                    bankAccount={selectedBankAccount}
                    onChange={() => setIsModalOpen(true)}
                    prefix={prefix}
                />
            ) : (
                <AddBankAccountButton
                    buttonTheme={buttonTheme}
                    onSuccess={handleAddBankAccount}
                    skipMFA={skipMFA}
                />
            )}
            {isModalOpen ? (
                <ChooseBankModal
                    onBack={() => setIsModalOpen(false)}
                    onSuccess={(bank) => {
                        setIsModalOpen(false);
                        handleSelectBankAccount(bank);
                        fetchBankAccounts();
                    }}
                    buttonTheme={buttonTheme}
                    skipMFA={skipMFA}
                />
            ) : null}
        </>
    );
};
